import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { FaFacebook, FaInstagram, FaLinkedin, FaYoutube, FaTiktok } from 'react-icons/fa';
import FundraiserHeader from "../common/FundraiserHeader";
import SwoodleLogo from "../../../Assets/Icons/SwoodleUpdatedLogo.svg";
import "./fundraiserbrand.scss";
import handHeartIcon from "../../../Assets/Icons/Fundraiser-marketing-icon.png";
import { useDispatch, useSelector } from "react-redux";
import { getFundraiserContent } from "../../../services/fundraiserService";
import Loader from "../../common/Loader";
import { useNavigate, useParams } from "react-router-dom";
import { getS3PublicUrl } from "../../../utils/s3";

const FundraiserBrandDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.fundraiserReducer?.loading);
  const id = useParams();
  const [content, setContent] = useState();

  const getContent = async () => {
    const getPromise = await dispatch(
      getFundraiserContent({ userId: id?.id }),
    )?.unwrap();
    if (getPromise?.success === true) {
      setContent(getPromise?.data);
    }
  };

  useEffect(() => {
    getContent();
  }, []);

  const handleJoinClick = () => {
    // New flow entry: go straight to new member setup with fundraiser context
    navigate(`/member-setup?fundraiserId=${content?.user?._id || id?.id}`);
  };
  const fundraiserName = content?.user?.name || "Fundraiser";
  const logoUrl = content?.logo ? getS3PublicUrl(content?.logo) : "";
  const bannerUrl = content?.banner ? getS3PublicUrl(content?.banner) : "";
  const websiteUrl = content?.website || "";
  const normalizedWebsiteHref = websiteUrl
    ? /^(https?:)?\/\//i.test(websiteUrl)
      ? websiteUrl
      : `https://${websiteUrl}`
    : "";

  return (
    <div className="fundraiser-brand-page w-full min-h-screen">
      <Helmet>
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content={`Support ${fundraiserName} on Swoodle`}
        />
        <meta
          property="og:description"
          content={`Join us in supporting ${fundraiserName}. Get amazing discounts while contributing to a great cause!`}
        />
        {content?.banner ? (
          <meta property="og:image" content={bannerUrl} />
        ) : null}

        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content={`Support ${fundraiserName} on Swoodle`}
        />
        <meta
          name="twitter:description"
          content={`Join us in supporting ${fundraiserName}. Get amazing discounts while contributing to a great cause!`}
        />
        {content?.banner ? (
          <meta name="twitter:image" content={bannerUrl} />
        ) : null}
      </Helmet>

      {loading === true && <Loader />}
      <FundraiserHeader brandLogo={null} />
      <div className="fundraiser-brand-shell">
        {/* Standalone Hero Banner Box */}
        <div className={`fundraiser-brand-hero-box ${bannerUrl ? "has-banner" : "no-banner"}`}>
          <div className={`hero-main-container ${bannerUrl ? "has-banner" : "no-banner"}`}>
            <div className="hero-left-content">
              <div className="hero-brand-row">
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt="Fundraiser Logo"
                    className="hero-logo"
                  />
                ) : null}
                <div className="hero-title-block">
                  <h1 className="hero-title">{fundraiserName}</h1>
                  <p className="hero-lead">
                    We are excited to invite you to join us in fundraising for the{" "}
                    <span className="hero-highlight">{fundraiserName}!</span>
                  </p>
                </div>
              </div>

              <div className="hero-cta-section">
                <h2 className="hero-cta-heading">Become a Swoodle Member</h2>
                
                <button
                  className="hero-join-button"
                  onClick={handleJoinClick}
                >
                  <span>Join now</span>
                  <span className="hero-join-button-arrow" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none">
                      <path
                        d="M5 12H19M13 6L19 12L13 18"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </button>

                <p className="hero-fineprint">
                  and unlock <span className="blue-bold">exclusive deals</span> and savings with <span className="blue-bold">40%</span> of your membership fee <span className="blue-bold">supports us!</span>{" "}
                  <span className="blue-heart-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 21C7.029 18.11 4 14.87 4 10.91C4 8.25 6.068 6 8.765 6C10.31 6 11.562 6.777 12 7.852C12.438 6.777 13.69 6 15.235 6C17.932 6 20 8.25 20 10.91C20 14.87 16.971 18.11 12 21Z"
                        stroke="currentColor"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </p>
              </div>
            </div>

            {bannerUrl ? (
              <div className="hero-right-banner">
                <div className="hero-banner-media-wrap">
                  <img
                    src={bannerUrl}
                    alt="Fundraiser Banner"
                    className="hero-banner-image sharp-layer"
                  />
                  <img
                    src={bannerUrl}
                    alt="Fundraiser Banner"
                    className="hero-banner-image blurred-layer"
                  />
                  <div className="hero-fade-overlay" />
                </div>
              </div>
            ) : null}
          </div>
          <div className="hero-bottom-bar" />
        </div>

        {/* About Us Section */}
        <section className="about-us-section">
          <h2 className="about-us-title">About us</h2>
          {websiteUrl ? (
            <a
              href={normalizedWebsiteHref}
              target="_blank"
              rel="noopener noreferrer"
              className="about-us-link"
            >
              {websiteUrl.replace(/^(https?:\/\/)?(www\.)?/, "")}
            </a>
          ) : null}
          
          {content?.aboutUs ? (
            <div className="about-us-pill">
              <div
                className="rich-text-content fundraiser-brand-copy"
                dangerouslySetInnerHTML={{ __html: content?.aboutUs }}
              />
            </div>
          ) : null}
        </section>

        {/* Support Section */}
        <section className="support-section">
          <span className="support-subtitle">WHY WE NEED YOUR SUPPORT</span>
          <div
            className="rich-text-content fundraiser-brand-copy"
            dangerouslySetInnerHTML={{ __html: content?.support || "" }}
          />
        </section>

        {/* Bottom CTA Card Section */}
        <section className="cta-card-section">
          <div className="cta-card">
            <div className="cta-card-left">
              <div className="cta-icon-circle">
                <img
                  src={handHeartIcon}
                  alt="Hand Heart Icon"
                  className="cta-heart-hand-img"
                />
              </div>
              
              <div className="cta-card-copy">
                <h3 className="cta-card-title">
                  Join us today and help <span className="highlight-text">{fundraiserName}</span> achieve their goals.
                </h3>
                <p className="cta-card-subtitle">
                  Your contribution matters, together we can continue to inspire, connect, and grow.
                </p>
              </div>
            </div>

            <div className="cta-card-right">
              <button
                className="cta-card-button"
                onClick={handleJoinClick}
              >
                <span>Join now</span>
                <span className="cta-card-button-arrow" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none">
                    <path
                      d="M5 12H19M13 6L19 12L13 18"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </button>
              
              <div className="cta-secure-badge">
                <span className="cta-secure-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    <path d="M9 11l2 2 4-4" />
                  </svg>
                </span>
                <span>Secure, trusted community driven.</span>
              </div>
            </div>
          </div>
        </section>

      </div>

      {/* Simplified Custom Swoodle Footer */}
      <footer className="fundraiser-brand-footer">
        <div className="footer-container">
          <div className="footer-left">
            <img src={SwoodleLogo} alt="Swoodle" className="footer-logo" />
            <p className="footer-text">
              Fundraising made simple for clubs, schools and communities.
            </p>
          </div>
          <div className="footer-right">
            <span className="footer-follow-us">FOLLOW US</span>
            <div className="footer-social-links">
              <a
                href="https://www.instagram.com/swoodleau/"
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon-btn"
                aria-label="Instagram"
              >
                <FaInstagram className="social-icon-svg" />
              </a>
              <a
                href="https://www.facebook.com/swoodleau/"
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon-btn"
                aria-label="Facebook"
              >
                <FaFacebook className="social-icon-svg" />
              </a>
              <a
                href="https://www.youtube.com/@swoodleau"
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon-btn"
                aria-label="YouTube"
              >
                <FaYoutube className="social-icon-svg" />
              </a>
              <a
                href="https://www.linkedin.com/company/swoodleau/"
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon-btn"
                aria-label="LinkedIn"
              >
                <FaLinkedin className="social-icon-svg" />
              </a>
              <a
                href="https://www.tiktok.com/@swoodleau"
                target="_blank"
                rel="noopener noreferrer"
                className="social-icon-btn"
                aria-label="TikTok"
              >
                <FaTiktok className="social-icon-svg" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FundraiserBrandDetails;
